import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { CheckUserResponse } from 'services/auth';
import { doCheckUser, doLogin } from 'store/actionCreators/auth';

import {
  CognitoError,
  getAmplifyAuthErrorMessage,
  mightBeSocialAccountError,
} from '../utils';
import { useSignInForm } from './form';

type SocialProvider = 'google' | 'apple' | 'facebook';

const normalizeProviders = (providers: string[] = []): SocialProvider[] => {
  const normalized = providers.map((p) => p.toLowerCase());
  const result: SocialProvider[] = [];
  if (normalized.some((p) => p.includes('google'))) result.push('google');
  if (normalized.some((p) => p.includes('apple'))) result.push('apple');
  if (normalized.some((p) => p.includes('facebook'))) result.push('facebook');
  return result;
};

export const useSignIn = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { handleSubmit, setValue, ...form } = useSignInForm();
  const [authError, setAuthError] = useState<string | null>(null);
  const [showSignupCta, setShowSignupCta] = useState(false);
  const [isPrecheckLoading, setIsPrecheckLoading] = useState(false);
  const [highlightProviders, setHighlightProviders] = useState<
    SocialProvider[]
  >([]);
  const clearAuthError = () => setAuthError(null);
  const clearSignupCta = () => setShowSignupCta(false);
  const clearHighlights = () => setHighlightProviders([]);

  const onFailure = (error: CognitoError) => {
    const title = getAmplifyAuthErrorMessage(t, error);

    // Only show social login warning if we can reasonably determine
    // the account is linked to social providers
    //
    // IMPORTANT: UserNotFoundException alone is NOT a reliable indicator
    // because it can occur for:
    // - Pre-registered accounts not yet fully set up in Cognito
    // - Non-existent accounts
    // - Deleted accounts
    //
    // We only show social warning if:
    // 1. Error message explicitly mentions social providers, OR
    // 2. We have other indicators (e.g., NotAuthorizedException with social indicators)
    const isLikelySocialAccount = mightBeSocialAccountError(error);

    if (isLikelySocialAccount) {
      setAuthError(t('auth.socialLoginDetected.message'));
      setShowSignupCta(false);
    } else {
      setAuthError(title);
      setShowSignupCta(false);
    }
  };

  const onSubmit = handleSubmit(async (formData) => {
    try {
      clearAuthError();
      clearSignupCta();
      clearHighlights();
      setIsPrecheckLoading(true);

      const normalizedEmail = formData.email.toLowerCase();
      const check = await new Promise<CheckUserResponse>((resolve, reject) => {
        dispatch(
          doCheckUser.trigger({
            email: normalizedEmail,
            onSuccess: resolve,
            onFailure: reject,
          }),
        );
      });

      if (check.status === 'NOT_FOUND') {
        setAuthError(t('auth.userDoesNotExist'));
        setShowSignupCta(true);
        return;
      }

      if (check.status === 'SOCIAL_LOGIN') {
        setHighlightProviders(
          normalizeProviders(check.socialLoginProviders || []),
        );
        const providers =
          check.socialLoginProviders.length > 0
            ? check.socialLoginProviders.join(', ')
            : null;

        setAuthError(
          providers
            ? t('auth.social_login_required', { providers })
            : t('auth.socialLoginDetected.message'),
        );
        setShowSignupCta(false);
        return;
      }

      dispatch(
        doLogin({
          email: normalizedEmail,
          password: formData.password,
          onFailure,
        }),
      );
    } catch (error: unknown) {
      if ((error as { isAxiosError?: boolean } | null)?.isAxiosError) {
        setAuthError(t('errors.text500'));
        setShowSignupCta(false);
        return;
      }
      onFailure(error as CognitoError);
    } finally {
      setIsPrecheckLoading(false);
    }
  });

  return {
    onSubmit,
    setValue,
    authError,
    clearAuthError,
    showSignupCta,
    clearSignupCta,
    isPrecheckLoading,
    highlightProviders,
    clearHighlights,
    ...form,
  };
};
